import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

const HOLD_TIME = 5000; // 5 seconds

const AnimatMASAImpactViewer = () => {
    const [impacts, setImpacts] = useState([]);
    const [counts, setCounts] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [imageCache, setImageCache] = useState({});
    const hasAnimated = useRef(false);
    
    const API = process.env.REACT_APP_API_BASE_URL;
    const API_IMPACT = `${API}/UImasa-impact`;
    const API_MEDIA = `${API}/UImedia`;
    const token = localStorage.getItem("token");

    /* 📡 Fetch Active Impacts - Tumhara original code use kiya */
    const fetchImpacts = async () => {
        try {
            setLoading(true);
            const res = await axios.get(API_IMPACT, {
                headers: { Authorization: `Bearer ${token}` },
            });

            // Sirf active impacts filter karo aur sort by number
            const impactsData = (res.data.results || res.data)
                .filter(impact => impact.isActive === true)
                .sort((a, b) => a.number - b.number);

            // Tumhara original loadIcons function use karo
            const impactsWithIcons = await loadIcons(impactsData);
            setImpacts(impactsWithIcons);
            
            // Count animation ke liye initial values set karo
            setCounts(impactsWithIcons.map(() => 0));
            
            // Animation start karo jab data ready ho
            setTimeout(() => {
                if (!hasAnimated.current && impactsWithIcons.length > 0) {
                    hasAnimated.current = true;
                    impactsWithIcons.forEach((item, index) =>
                        animateCounter(item.count || item.number || 0, index)
                    );
                }
            }, 100);
        } catch (err) {
            console.error("❌ Error fetching impacts:", err);
        } finally {
            setLoading(false);
        }
    };

    /* 🖼️ Load Icons - Tumhara original function */
    const loadIcons = async (impactsData) => {
        const updated = await Promise.all(
            impactsData.map(async (impact) => {
                // Agar cache mein hai toh wahi use karo
                if (imageCache[impact.id]) {
                    return { ...impact, iconUrl: imageCache[impact.id] };
                }

                if (!impact.icon) {
                    const fallbackUrl = "https://via.placeholder.com/120/92400E/D97706?text=MASA";
                    setImageCache(prev => ({ ...prev, [impact.id]: fallbackUrl }));
                    return { ...impact, iconUrl: fallbackUrl };
                }

                try {
                    const res = await axios.get(
                        `${API_MEDIA}/${impact.icon}/download`,
                        {
                            headers: { Authorization: `Bearer ${token}` },
                            responseType: "blob",
                        }
                    );
                    const imageUrl = URL.createObjectURL(res.data);

                    // Cache mein store karo
                    setImageCache(prev => ({ ...prev, [impact.id]: imageUrl }));

                    return { ...impact, iconUrl: imageUrl };
                } catch {
                    const fallbackUrl = "https://via.placeholder.com/120/92400E/D97706?text=MASA";
                    setImageCache(prev => ({ ...prev, [impact.id]: fallbackUrl }));
                    return { ...impact, iconUrl: fallbackUrl };
                }
            })
        );

        return updated;
    };

    /* COUNTER ANIMATION - Tumhara original animation logic */
    const animateCounter = (target, index) => {
        const duration = 2000;
        const start = performance.now();

        const step = (now) => {
            const progress = Math.min((now - start) / duration, 1);
            const ease = 1 - Math.pow(1 - progress, 4);
            const currentValue = Math.floor(ease * target);

            setCounts((prev) => {
                const copy = [...prev];
                copy[index] = currentValue;
                return copy;
            });

            if (progress < 1) {
                requestAnimationFrame(step);
            }
        };

        requestAnimationFrame(step);
    };

    /* STEP SLIDER LOGIC */
    useEffect(() => {
        if (impacts.length === 0) return;
        
        const totalSteps = Math.ceil(impacts.length / 2);
        if (totalSteps <= 1) return;

        const interval = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % totalSteps);
        }, HOLD_TIME);

        return () => clearInterval(interval);
    }, [impacts.length]);

    /* Initial fetch */
    useEffect(() => {
        fetchImpacts();
        // eslint-disable-next-line
    }, []);

    /* group cards in pairs */
    const rows = [];
    for (let i = 0; i < impacts.length; i += 2) {
        rows.push(impacts.slice(i, i + 2));
    }

    /* ================= CSS ================= */
    const styles = `
    /* Desktop aur Tablet ke liye default styles */
    .slider-track {
      display: flex;
      transition: transform 0.8s ease-in-out;
      width: 100%;
    }

    .card-row {
      display: flex;
      width: 100%;
      gap: 20px;
      flex-shrink: 0;
      padding: 0 24px;
    }

    /* CARD BASE */
    .impact-card {
      position: relative;
      width: 50%;
      background: white;
      border-radius: 0px 0 0 50px;
      padding: 14px 20px;
      overflow: hidden;
    }

    /* GRADIENT BORDER */
    .impact-card::before {
      content: "";
      position: absolute;
      inset: 1;
      padding: 2px;
      border-radius: 12px 0 0 12px;
      background: linear-gradient(90deg,#ff8a00,#ffb347,#ff8a00);
      -webkit-mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
      z-index: 1;
    }

    /* 🔥 MAGIC PART – BG + BORDER FADE + RIBBON */
    .impact-card::after {
      content: "";
      position: absolute;
      inset: 0;
      border-radius: 12px 0 0 12px;
      pointer-events: none;
      background: linear-gradient(
        to right,
        rgba(255,255,255,0) 0%,
        rgba(255,255,255,0) 35%,
        rgba(255,255,255,0.6) 55%,
        rgba(255,255,255,0.85) 65%,
        #FFD1A3 78%,
        #FFC08A 88%,
        #FFC08A 100%
      );
      z-index: 0;
    }

    /* CONTENT ABOVE EVERYTHING */
    .impact-card > * {
      position: relative;
      z-index: 2;
    }
    
    .icon-circle {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      border: 2px solid #f97316;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      flex-shrink: 0;
    }

    .icon-circle img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .placeholder-icon {
      color: #f97316;
      font-size: 16px;
    }

    /* COUNTER TEXT */
    .counter-text {
      font-size: 40px;
      font-weight: 800;
      color: #1f2937;
    }

    /* TITLE TEXT */
    .title-text {
      font-size: 30px;
      font-weight: 500;
      color: #1f2937;
    }

    /* 📱 MOBILE RESPONSIVE STYLES */
    @media (max-width: 768px) {
      .card-row {
        flex-direction: column;
        gap: 12px;
        padding: 0 16px;
      }
      
      .impact-card {
        width: 100% !important;
        border-radius: 0px 0 0 30px !important;
        padding: 12px 16px !important;
        margin-bottom: 0 !important;
        display: flex !important;
        align-items: center !important;
        gap: 12px !important;
      }
      
      .impact-card::before {
        border-radius: 8px 0 0 8px !important;
      }
      
      .impact-card::after {
        border-radius: 8px 0 0 8px !important;
      }
      
      .icon-circle {
        width: 32px;
        height: 32px;
        flex-shrink: 0;
      }
      
      .placeholder-icon {
        font-size: 14px;
      }
      
      .counter-text {
        font-size: 32px !important;
        flex-shrink: 0;
        min-width: fit-content;
      }
      
      .title-text {
        font-size: 20px !important;
        white-space: normal !important;
        word-wrap: break-word !important;
        overflow-wrap: break-word !important;
        line-height: 1.2 !important;
        flex-grow: 1;
        overflow: visible !important;
      }
      
      .impact-card .h-10 {
        height: 32px !important;
      }
      
      .w-\[3px\] {
        width: 2px !important;
      }
      
      /* Mobile mein slider ko center align karo */
      .slider-track {
        align-items: center;
      }
      
      /* Text overflow fix */
      .impact-card .whitespace-nowrap {
        white-space: normal !important;
      }
    }
    
    /* Small mobile devices ke liye */
    @media (max-width: 480px) {
      .card-row {
        padding: 0 12px;
        gap: 10px;
      }
      
      .impact-card {
        padding: 10px 14px !important;
        border-radius: 0px 0 0 25px !important;
        gap: 10px !important;
      }
      
      .counter-text {
        font-size: 28px !important;
      }
      
      .title-text {
        font-size: 18px !important;
        line-height: 1.1 !important;
      }
      
      .icon-circle {
        width: 28px;
        height: 28px;
      }
      
      .placeholder-icon {
        font-size: 12px;
      }
      
      .impact-card .h-10 {
        height: 28px !important;
      }
      
      /* Very small screens pe layout adjust */
      @media (max-width: 360px) {
        .impact-card {
          flex-wrap: wrap;
          gap: 8px !important;
        }
        
        .counter-text {
          font-size: 24px !important;
        }
        
        .title-text {
          font-size: 16px !important;
          width: 100%;
          margin-left: 40px; /* icon ke width ke equal */
        }
      }
    }
    
    /* Tablet devices ke liye */
    @media (min-width: 769px) and (max-width: 1024px) {
      .counter-text {
        font-size: 36px !important;
      }
      
      .title-text {
        font-size: 24px !important;
      }
      
      .card-row {
        padding: 0 20px;
      }
    }
  `;

    if (loading) {
        return (
            <div className="w-full my-12">
                <div className="w-full bg-gradient-to-r from-[#FFD1A3] via-[#FFC08A] to-[#FFB066] p-8">
                    <div className="text-center text-gray-700">Loading impacts...</div>
                </div>
            </div>
        );
    }

    if (impacts.length === 0) {
        return (
            <div className="w-full my-12">
                <div className="w-full bg-gradient-to-r from-[#FFD1A3] via-[#FFC08A] to-[#FFB066] p-8">
                    <div className="text-center text-gray-700">No impacts found</div>
                </div>
            </div>
        );
    }

    return (
        <>
            <style>{styles}</style>

            <div className="w-full my-12 overflow-hidden">
                <div className="w-full bg-gradient-to-r from-[#FFD1A3] via-[#FFC08A] to-[#FFB066] ">

                    {/* SLIDER */}
                    <div
                        className="slider-track"
                        style={{
                            transform: `translateX(-${currentIndex * 100}%)`,
                        }}
                    >
                        {rows.map((row, rowIndex) => (
                            <div className="card-row" key={rowIndex}>
                                {row.map((item, index) => {
                                    const realIndex = rowIndex * 2 + index;

                                    return (
                                        <div
                                            key={realIndex}
                                            className="impact-card flex items-center gap-3"
                                        >
                                            <div className="icon-circle">
                                                {item.iconUrl ? (
                                                    <img 
                                                        src={item.iconUrl} 
                                                        alt={item.title || "Impact icon"} 
                                                        onError={(e) => {
                                                            e.target.onerror = null;
                                                            e.target.style.display = 'none';
                                                            e.target.parentElement.innerHTML = 
                                                                '<div class="placeholder-icon">M</div>';
                                                        }}
                                                    />
                                                ) : (
                                                    <div className="placeholder-icon">M</div>
                                                )}
                                            </div>

                                            <div className="counter-text font-extrabold text-gray-900">
                                                {counts[realIndex]?.toLocaleString()}
                                            </div>

                                            <div className="w-[3px] h-10 bg-gray-500"></div>

                                            <div className="title-text font-medium text-gray-900">
                                                {item.title || "Impact Title"}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>

                </div>
            </div>
        </>
    );
};

export default AnimatMASAImpactViewer;